import { put, select } from 'redux-saga/effects';
import { requestWhiteboardData, signalingWhiteboardModeChanged } from 'features/whiteboard/actions';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import {
  ContentLibraryOpenedFile,
  ContentLibraryOpenedWhiteboard,
} from 'features/content-library/types';
import { whiteboardViewed } from 'features/content-library/actions';
import {
  selectContentLibraryActiveWhiteboards,
  selectContentLibraryOpenedFile,
  activeWhiteboardsCollectionUpdated,
} from 'features/content-library/contentLibrarySlice';
import {
  signalingWhiteboardClosed,
  signalingWhiteboardOpened,
} from 'features/layout/features/content/actions';
import { ContentData, GridPanelName } from 'features/layout/features/content/types';
import {
  gridPanelOpened,
  selectGridPanel,
  lockedContentChanged,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import { selectCanManagePrivateWhiteboards } from 'features/permissions/permissionsSlice';
import { formatToWhiteboardFile } from 'features/content-library/utils/format';

export function* onWhiteboardModeChangedSaga(
  action: ReturnType<typeof signalingWhiteboardModeChanged>
) {
  const {
    initiator,
    data: { id, private: isPrivate, name, opened },
  } = action.payload;

  if (isPrivate) {
    const lockedContent: ContentData = yield select(selectLockedContent);
    if (lockedContent && lockedContent.type === 'whiteboard' && lockedContent.id === id) {
      yield put(lockedContentChanged(null));
    }
  }

  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);

  if (opened || (e2eeEnabled && opened === undefined)) {
    const canManagePrivateWhiteboard: boolean = yield select(selectCanManagePrivateWhiteboards);

    const activeWhiteboards: ContentLibraryOpenedWhiteboard[] = yield select(
      selectContentLibraryActiveWhiteboards
    );

    const isWhiteboardActive = activeWhiteboards.some((item) => item.id === id);

    if (isPrivate && isWhiteboardActive && !canManagePrivateWhiteboard) {
      yield put(signalingWhiteboardClosed({ id }));
      return;
    }

    if (!isPrivate && !isWhiteboardActive) {
      const lockedContent: ContentData = yield select(selectLockedContent);

      if (lockedContent && lockedContent.id !== id) {
        yield put(
          activeWhiteboardsCollectionUpdated({
            [id]: formatToWhiteboardFile({ id, name, shapes: [], private: isPrivate }),
          })
        );

        return;
      }

      yield put(
        signalingWhiteboardOpened({
          initiator,
          data: {
            id,
            name,
            shapes: [],
            private: isPrivate,
          },
        })
      );

      yield put(requestWhiteboardData(id));
      return;
    }

    const currentOpenedFile: ContentLibraryOpenedFile = yield select(
      selectContentLibraryOpenedFile
    );

    if (!isPrivate && isWhiteboardActive && currentOpenedFile.id !== id) {
      const lockedContent: ContentData = yield select(selectLockedContent);

      if (lockedContent && lockedContent.id !== id) {
        return;
      }

      yield put(whiteboardViewed(id));

      const currentGridPanel: GridPanelName = yield select(selectGridPanel);

      if (currentGridPanel !== 'whiteboard') {
        yield put(gridPanelOpened('whiteboard'));
      }
    }
  }
}
